import { useEffect, useRef, useState } from "react";
import { useSuiClientQuery } from "@mysten/dapp-kit";
import { useSuiAuth } from "@/components/providers/SuiAuthProvider";

/**
 * Operator account control, top-right of the shell. Connected Sui wallet →
 * dropdown with live SUI + USDC balance and sign-out. emil: the menu scales in
 * from its trigger (origin top-right), ease-out, 150ms; click-outside closes.
 */

function short(addr: string | null): string {
  if (!addr) return "—";
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}

function fmtBal(raw: string | undefined, decimals: number): string {
  if (!raw) return "0";
  const n = Number(raw) / 10 ** decimals;
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals >= 9 ? 4 : 2 });
}

export function SuiAccountMenu() {
  const { suiAddress, signOut } = useSuiAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: balances } = useSuiClientQuery(
    "getAllBalances",
    { owner: suiAddress ?? "" },
    { enabled: !!suiAddress, refetchInterval: 15_000 },
  );

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!suiAddress) return null;

  const sui = balances?.find((b) => b.coinType === "0x2::sui::SUI");
  const usdc = balances?.find((b) => /usdc/i.test(b.coinType));

  return (
    <div className="am" ref={ref}>
      <button
        type="button"
        className={`am__trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="am__dot" aria-hidden="true" />
        <span className="am__addr">{short(suiAddress)}</span>
        <svg className={`am__caret ${open ? "is-open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <div className={`am__menu ${open ? "is-open" : ""}`} role="menu">
        <div className="am__head">operator · sui testnet</div>
        <a
          className="am__addr-full"
          href={`https://testnet.suivision.xyz/account/${suiAddress}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {short(suiAddress)} <span className="am__ext">↗</span>
        </a>
        <div className="am__bals">
          <div className="am__bal"><span className="am__bal-k">SUI</span><span className="am__bal-v">{fmtBal(sui?.totalBalance, 9)}</span></div>
          <div className="am__bal"><span className="am__bal-k">USDC</span><span className="am__bal-v">{fmtBal(usdc?.totalBalance, 6)}</span></div>
        </div>
        <button type="button" className="am__logout" role="menuitem" onClick={() => { setOpen(false); signOut(); }}>
          Sign out
        </button>
      </div>

      <style>{`
        .am { position: relative; }
        .am__trigger {
          display: inline-flex; align-items: center; gap: var(--s-2);
          height: 34px; padding: 0 12px;
          border: 1px solid var(--line); border-radius: var(--radius-pill);
          background: var(--bg-elev); color: var(--bone);
          font: var(--type-mono-address);
          transition: border-color 150ms ease, background 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1);
        }
        .am__trigger:hover { border-color: var(--line-stamp); }
        .am__trigger:active { transform: scale(0.98); }
        .am__trigger.is-open { border-color: var(--indigo); background: var(--indigo-tint); }
        .am__dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); flex: none; }
        .am__addr { letter-spacing: 0; }
        .am__caret { color: var(--slate-2); transition: transform 150ms cubic-bezier(0.23,1,0.32,1); }
        .am__caret.is-open { transform: rotate(180deg); }

        .am__menu {
          position: absolute; top: calc(100% + 8px); right: 0; z-index: 40;
          width: 240px; padding: var(--s-3);
          background: var(--bg-elev2); border: 1px solid var(--line);
          border-radius: var(--r-md); box-shadow: 0 18px 40px -16px rgba(27,16,96,0.28);
          transform-origin: top right; opacity: 0; transform: scale(0.96);
          pointer-events: none;
          transition: opacity 150ms cubic-bezier(0.23,1,0.32,1), transform 150ms cubic-bezier(0.23,1,0.32,1);
        }
        .am__menu.is-open { opacity: 1; transform: scale(1); pointer-events: auto; }
        .am__head { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); padding: 0 var(--s-2) var(--s-2); }
        .am__addr-full { display: block; font: var(--type-mono-data); color: var(--bone); padding: 0 var(--s-2) var(--s-3); border-bottom: 1px solid var(--line-soft); }
        .am__addr-full:hover { color: var(--accent-deep); }
        .am__ext { color: var(--slate-3); }
        .am__bals { display: flex; flex-direction: column; gap: 2px; padding: var(--s-3) 0; }
        .am__bal { display: flex; align-items: baseline; justify-content: space-between; padding: 6px var(--s-2); }
        .am__bal-k { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate-2); }
        .am__bal-v { font-family: var(--font-mono); font-variant-numeric: tabular-nums; font-size: 14px; color: var(--bone); }
        .am__logout {
          display: block; width: 100%; text-align: left;
          padding: 10px var(--s-2); margin-top: var(--s-2);
          border-top: 1px solid var(--line-soft);
          font: var(--type-body-small); color: var(--oxblood);
          transition: color 120ms ease;
        }
        .am__logout:hover { color: var(--oxblood-bright); }

        @media (prefers-reduced-motion: reduce) {
          .am__menu, .am__caret, .am__trigger { transition: opacity 120ms ease !important; }
        }
      `}</style>
    </div>
  );
}
